// Classificação dos lançamentos em "A Classificar" (etapa 2 do fechamento).
// Puro (sem React, sem Supabase).

import { normalizarTexto } from '../extrato/comum';
import { classificar, codigoDaConta, compararCodigos } from '../extrato/montarImportacao';

export const CONTA_A_CLASSIFICAR = '9.9.9 — A Classificar';
export const CODIGO_A_CLASSIFICAR = '9.9.9';

// Palavras do extrato que dizem o tipo da operação, não quem é a outra parte.
const RUIDO = new Set([
  'pix', 'ted', 'doc', 'tev', 'transf', 'transferencia', 'transferencias', 'enviado', 'enviada', 'enviados',
  'recebido', 'recebida', 'recebidos', 'receb', 'env', 'pagamento', 'pagamentos', 'pagto', 'pgto', 'pag',
  'compra', 'compras', 'cartao', 'debito', 'credito', 'deb', 'cred', 'automatico', 'aut', 'online',
  'para', 'de', 'da', 'do', 'das', 'dos', 'em', 'no', 'na', 'via', 'ref', 'referente', 'conta', 'agencia',
  'ag', 'cc', 'cpf', 'cnpj', 'mesma', 'titularidade', 'outra', 'outras', 'instituicao', 'banco', 'qr', 'code',
  'qrcode', 'chave', 'liquidacao', 'liq', 'favorecido', 'remetente', 'origem', 'destino', 'lancamento',
  'boleto', 'boletos',
]);

// Ligam palavras de um nome ("João da Silva"): ficam no meio do trecho, nunca nas pontas.
const CONECTORES = new Set(['de', 'da', 'do', 'das', 'dos', 'e']);

const letras = (t) => (t.match(/[a-z]/g) || []).length;
const digitos = (t) => (t.match(/\d/g) || []).length;

function tokenSignificativo(raw) {
  const t = raw.replace(/[^a-z0-9]/g, '');
  if (!t || RUIDO.has(t)) return false;
  if (letras(t) < 3) return false;
  return digitos(t) <= letras(t); // código/protocolo misturado com letras não serve
}

/**
 * Sugere o trecho da descrição para a regra: o maior bloco contíguo de palavras
 * significativas (até 3), sem datas, números, CPF/CNPJ e palavras de tipo de
 * operação (PIX, TED, ENVIADO...). O resultado é sempre um trecho contíguo da
 * descrição normalizada, porque a regra casa por "contém".
 */
export function sugerirTrechoRegra(descricao) {
  const norm = normalizarTexto(descricao);
  if (!norm) return '';
  const tokens = norm.split(' ');
  const signif = (run) => run.filter(t => !CONECTORES.has(t)).length;
  const semConectorNoFim = (run) => {
    const r = run.slice();
    while (r.length && CONECTORES.has(r[r.length - 1])) r.pop();
    return r;
  };
  let melhor = [];
  let atual = [];
  const fechar = () => {
    const r = semConectorNoFim(atual);
    if (signif(r) > signif(melhor)) melhor = r;
    atual = [];
  };
  tokens.forEach(tk => {
    if (tokenSignificativo(tk)) atual.push(tk);
    else if (CONECTORES.has(tk) && atual.length) atual.push(tk); // "joao da silva"
    else fechar();
  });
  fechar();
  if (!melhor.length) return '';
  // Até três palavras significativas, mantendo os conectores do meio.
  const corte = [];
  let n = 0;
  for (const t of melhor) {
    if (!CONECTORES.has(t)) n += 1;
    if (n > 3) break;
    corte.push(t);
  }
  const trecho = semConectorNoFim(corte).join(' ');
  // Tira pontuação das pontas (o miolo continua contíguo ao texto original).
  return trecho.replace(/^[^a-z0-9]+/, '').replace(/[^a-z0-9]+$/, '');
}

// Contas analíticas (sem filhas no plano), sem a 9.9.9, ordenadas por código.
export function contasAnaliticas(plano = []) {
  const lista = plano || [];
  const codigos = lista.map(c => String(c.codigo || ''));
  return lista
    .filter(c => c && c.codigo && String(c.codigo) !== CODIGO_A_CLASSIFICAR)
    .filter(c => !codigos.some(o => o.startsWith(`${c.codigo}.`)))
    .map(c => ({ codigo: String(c.codigo), nome: c.nome, rotulo: `${c.codigo} — ${c.nome}` }))
    .sort((a, b) => compararCodigos(a.codigo, b.codigo));
}

/**
 * Lançamentos do banco no mês com um lado em "A Classificar".
 * Lado crédito nulo = o banco foi debitado = entrada; lado débito nulo = saída.
 */
export function pendentesDeClassificacao(lancamentos = [], periodo) {
  return (lancamentos || [])
    .filter(l => l && l._db && (l.data || '').slice(0, 7) === periodo)
    .filter(l => codigoDaConta(l.contaDebito) === CODIGO_A_CLASSIFICAR || codigoDaConta(l.contaCredito) === CODIGO_A_CLASSIFICAR)
    .map(l => {
      const debNulo = codigoDaConta(l.contaDebito) === CODIGO_A_CLASSIFICAR;
      const credNulo = codigoDaConta(l.contaCredito) === CODIGO_A_CLASSIFICAR;
      return {
        id: l._dbId,
        data: l.data,
        historico: l.historico || '',
        valor: Number(l.valor) || 0,
        // Se os dois lados estiverem em A Classificar (não acontece pela constraint), fica o débito.
        campo: debNulo ? 'conta_debito' : 'conta_credito',
        sentido: credNulo && !debNulo ? 'entrada' : 'saida',
        contaOutroLado: debNulo ? (credNulo ? '' : l.contaCredito) : l.contaDebito,
        origem: l.origem || 'manual',
      };
    })
    .sort((a, b) => (a.data < b.data ? -1 : a.data > b.data ? 1 : a.historico.localeCompare(b.historico, 'pt-BR')));
}

// A conta escolhida não pode ser a mesma do outro lado do lançamento.
export function contaValidaPara(pendente, conta) {
  if (!pendente || !conta) return false;
  const cod = codigoDaConta(conta);
  if (!cod || cod === CODIGO_A_CLASSIFICAR) return false;
  return cod !== codigoDaConta(pendente.contaOutroLado);
}

/**
 * Pendentes (exceto `excluirId`) que uma regra nova casaria. Mesma função de
 * casamento da importação (classificar), para o número mostrado ser o aplicado.
 */
export function pendentesQueCasam(pendentes = [], { padrao, sentido = 'ambos', conta }, excluirId) {
  const p = normalizarTexto(padrao);
  if (!p) return [];
  const regra = [{ padrao: p, conta, sentido }];
  return (pendentes || []).filter(x => x.id !== excluirId
    && classificar(x.historico, x.sentido, regra)
    && contaValidaPara(x, conta));
}

/**
 * Aplica um conjunto de regras (saída de montarRegras) aos pendentes.
 * Devolve [{ pendente, conta, padrao }] só para os que casaram com conta válida.
 */
export function aplicarRegrasAosPendentes(pendentes = [], regras = []) {
  const out = [];
  (pendentes || []).forEach(x => {
    const r = classificar(x.historico, x.sentido, regras);
    if (r && contaValidaPara(x, r.conta)) out.push({ pendente: x, conta: r.conta, padrao: r.padrao });
  });
  return out;
}

// Agrupa as atualizações por campo e conta: uma chamada ao banco por grupo.
// itens: [{ pendente, conta }] → [{ campo, conta, ids: [] }]
export function agruparAtualizacoes(itens = []) {
  const grupos = new Map();
  itens.forEach(({ pendente, conta }) => {
    const k = `${pendente.campo}||${conta}`;
    if (!grupos.has(k)) grupos.set(k, { campo: pendente.campo, conta, ids: [] });
    grupos.get(k).ids.push(pendente.id);
  });
  return [...grupos.values()];
}
